import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2Icon, ShieldCheckIcon, StarIcon, ChevronLeftIcon, ChevronRightIcon, ZapIcon, AwardIcon, HomeIcon, AppWindowIcon, LayersIcon, ChevronDownIcon, SunIcon, DoorOpenIcon } from 'lucide-react';
import { BrandedQuoteForm } from './BrandedQuoteForm';
import { ChampionLogo } from './ChampionLogo';
interface BrandedLandingPageProps {
  initialService?: string;
  onClose: () => void;
}
export function BrandedLandingPage({
  initialService,
  onClose
}: BrandedLandingPageProps) {
  const [showForm, setShowForm] = useState(false);
  const [selectedService, setSelectedService] = useState<string>(initialService || '');
  const [zipCode, setZipCode] = useState('');
  const [isValidZip, setIsValidZip] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (initialService) {
      setSelectedService(initialService);
    }
  }, [initialService]);
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleZipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 5);
    setZipCode(val);
    setIsValidZip(val.length === 5);
  };
  const handleContinue = () => {
    if (isValidZip && selectedService) {
      setShowForm(true);
    }
  };
  if (showForm) {
    return <BrandedQuoteForm initialZip={zipCode} serviceSlug={selectedService} onClose={onClose} data-id="element-617" />;
  }
  const services = [{
    id: 'windows',
    name: 'Windows',
    icon: <AppWindowIcon className="w-6 h-6" data-id="element-618" />,
    desc: 'Window replacement'
  }, {
    id: 'sunrooms',
    name: 'Sunrooms',
    icon: <SunIcon className="w-6 h-6" data-id="element-619" />,
    desc: 'Sunroom additions'
  }, {
    id: 'siding',
    name: 'Siding',
    icon: <LayersIcon className="w-6 h-6" data-id="element-620" />,
    desc: 'Exterior siding'
  }];
  const serviceLabel = services.find(s => s.id === selectedService)?.name || '';
  const heroImages: Record<string, string> = {
    windows: 'https://cdn.magicpatterns.com/uploads/q7qR5NyHb39LHqKrvgXsGJ/Christina02_TheAfter_BayInterior.jpg',
    sunrooms: 'https://cdn.magicpatterns.com/uploads/sgJwcsLTG5TY7d8gwqwvoW/Coleman01_TheAfter_AllSeasonExterior_Gable.jpg',
    siding: 'https://cdn.magicpatterns.com/uploads/59DBb311HQHWayRMksaANo/siding_backgroun.webp'
  };
  const heroImage = heroImages[selectedService] || 'https://cdn.magicpatterns.com/uploads/q7qR5NyHb39LHqKrvgXsGJ/Christina02_TheAfter_BayInterior.jpg';
  const heroHeadlines: Record<string, React.ReactNode> = {
    windows: <>
        Replacement Windows
        <br data-id="element-621" />
        In Every Style
      </>,
    sunrooms: <>
        Change Your View
        <br data-id="element-622" />
        With a Sunroom
      </>,
    siding: <>
        Premium Vinyl
        <br data-id="element-623" />
        Siding
      </>
  };
  const heroHeadline = selectedService ? heroHeadlines[selectedService] : <>
      Replacement Windows
      <br data-id="element-624" />
      In Every Style
    </>;
  const serviceOffers: Record<string, {
    main: string;
    sub: string;
  }> = {
    windows: {
      main: 'Buy 2 Windows, Get 2 FREE*',
      sub: 'Plus, Pay Nothing for a Year**'
    },
    sunrooms: {
      main: '30% OFF* Sunrooms',
      sub: 'Plus, Pay Nothing for a Year**'
    },
    siding: {
      main: '30% OFF* Siding',
      sub: 'Plus, Pay Nothing for a Year**'
    }
  };
  const currentOffer = serviceOffers[selectedService] || serviceOffers.windows;
  const ChampionHeader = () => <header className="bg-white py-4 px-6 md:px-12 flex justify-between items-center shadow-sm relative z-20" data-id="element-625">
      <ChampionLogo size="md" data-id="element-626" />
      <div data-id="element-627" />
    </header>;
  return <div className="min-h-screen bg-[#F5F5F5] font-sans" data-id="element-628">
      <ChampionHeader data-id="element-629" />

      {/* Special Offer Banner */}
      <section className="bg-[#003DA5] py-6 md:py-8 border-t border-blue-800" data-id="element-630">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-631">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 md:gap-12" data-id="element-632">
            <div className="flex-1" data-id="element-633">
              <div className="inline-block bg-[#FFC107] text-black font-bold text-sm px-3 py-1 rounded-sm mb-3" data-id="element-634">
                Special Offer
              </div>
              <h2 className="text-3xl md:text-4xl font-extrabold text-white tracking-tight" data-id="element-635">
                {currentOffer.main}
              </h2>
            </div>
            <div className="hidden md:block w-px h-16 bg-white/20" data-id="element-636"></div>
            <div className="flex-1" data-id="element-637">
              <h3 className="text-xl md:text-2xl font-bold text-white mb-2" data-id="element-638">
                {currentOffer.sub}
              </h3>
              <p className="text-blue-100 text-sm md:text-base leading-relaxed" data-id="element-639">
                Interest is billed during promo period but will be waived if the
                amount financed is paid in full before promo period expires.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Hero Section */}
      <section className="relative pt-16 pb-24 md:pt-24 md:pb-32 overflow-hidden" data-id="element-640">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0" data-id="element-641">
          {!selectedService || selectedService === 'windows' ? <video src="https://cdn.magicpatterns.com/uploads/9n8mGA2gPPNcpr9Fs2AgPC/homepage-hero.mp4" autoPlay loop muted playsInline className="w-full h-full object-cover transition-all duration-700" data-id="element-642" /> : selectedService === 'sunrooms' ? <video src="https://cdn.magicpatterns.com/uploads/h8dgfU1MwkH4sZp6mwe8E9/sunrooms-hero.mp4" autoPlay loop muted playsInline className="w-full h-full object-cover transition-all duration-700" data-id="element-643" /> : <img src={heroImage} alt="Beautiful home exterior" className="w-full h-full object-cover transition-all duration-700" data-id="element-644" />}
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent" data-id="element-645"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-646">
          <div className="flex flex-col md:flex-row items-center justify-between gap-12" data-id="element-647">
            {/* Left Content */}
            <motion.div className="flex-1 text-white" initial={{
            opacity: 0,
            x: -30
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.6
          }} data-id="element-648">
              <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight mb-4 leading-tight drop-shadow-lg" data-id="element-649">
                {heroHeadline}
              </h1>
              <p className="text-xl md:text-2xl font-medium text-gray-200 drop-shadow-md" data-id="element-650">
                {currentOffer.sub}
              </p>
            </motion.div>

            {/* Right Form Card */}
            <motion.div className="w-full max-w-md" initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} data-id="element-651">
              <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-100" data-id="element-652">
                <h2 className="text-2xl font-bold text-[#003DA5] text-center mb-6" data-id="element-653">
                  {initialService ? 'Enter Your Zip Code To Start' : 'Select a Service & Enter Your Zip'}
                </h2>

                {!initialService && <div className="mb-6 relative" ref={dropdownRef} data-id="element-654">
                    <button onClick={() => setDropdownOpen(!dropdownOpen)} className={`w-full flex items-center justify-between px-5 py-4 rounded-xl border-2 transition-all duration-200 bg-blue-50 ${selectedService ? 'border-[#003DA5]' : 'border-blue-200'} ${dropdownOpen ? 'border-[#003DA5] shadow-md' : 'hover:border-blue-300'}`} data-id="element-655">
                      <div className="flex items-center gap-3" data-id="element-656">
                        {selectedService ? <>
                            <span className="text-[#003DA5]" data-id="element-657">
                              {services.find(s => s.id === selectedService)?.icon}
                            </span>
                            <span className="font-bold text-[#003DA5] text-lg" data-id="element-658">
                              {services.find(s => s.id === selectedService)?.name}
                            </span>
                          </> : <span className="text-gray-400 font-semibold text-lg" data-id="element-659">
                            Choose a service...
                          </span>}
                      </div>
                      <ChevronDownIcon className={`w-5 h-5 text-[#003DA5] transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`} data-id="element-660" />
                    </button>

                    <AnimatePresence data-id="element-661">
                      {dropdownOpen && <motion.div initial={{
                    opacity: 0,
                    y: -8,
                    scaleY: 0.95
                  }} animate={{
                    opacity: 1,
                    y: 0,
                    scaleY: 1
                  }} exit={{
                    opacity: 0,
                    y: -8,
                    scaleY: 0.95
                  }} transition={{
                    duration: 0.15,
                    ease: 'easeOut'
                  }} className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl border-2 border-blue-200 shadow-xl z-30 overflow-hidden origin-top" data-id="element-662">
                          {services.map(svc => <button key={svc.id} onClick={() => {
                      setSelectedService(svc.id);
                      setDropdownOpen(false);
                    }} className={`w-full flex items-center gap-3 px-5 py-4 text-left transition-colors duration-150 ${selectedService === svc.id ? 'bg-blue-50 text-[#003DA5]' : 'bg-white text-gray-700 hover:bg-blue-50 hover:text-[#003DA5]'} border-b border-blue-100 last:border-b-0`} data-id="element-663">
                              <span className={`${selectedService === svc.id ? 'text-[#003DA5]' : 'text-gray-400'}`} data-id="element-664">
                                {svc.icon}
                              </span>
                              <span className="font-bold text-lg" data-id="element-665">
                                {svc.name}
                              </span>
                              {selectedService === svc.id && <CheckCircle2Icon className="w-5 h-5 text-[#003DA5] ml-auto" data-id="element-666" />}
                            </button>)}
                        </motion.div>}
                    </AnimatePresence>
                  </div>}

                {initialService && <div className="flex items-center justify-center gap-2 mb-6 bg-blue-50 border border-blue-200 rounded-lg px-4 py-2.5" data-id="element-667">
                    <span className="text-[#003DA5]" data-id="element-668">
                      {services.find(s => s.id === initialService)?.icon}
                    </span>
                    <span className="font-bold text-[#003DA5]" data-id="element-669">
                      {serviceLabel}
                    </span>
                  </div>}

                <div className="relative mb-6" data-id="element-670">
                  <input type="text" value={zipCode} onChange={handleZipChange} placeholder="Zip Code" className="w-full h-16 px-6 text-2xl font-bold text-center bg-white border-2 border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:border-[#003DA5] focus:ring-1 focus:ring-[#003DA5] transition-colors" maxLength={5} data-id="element-671" />
                  {isValidZip && <div className="absolute right-4 top-1/2 -translate-y-1/2 text-green-500" data-id="element-672">
                      <CheckCircle2Icon className="w-8 h-8" data-id="element-673" />
                    </div>}
                </div>

                <button onClick={handleContinue} disabled={!isValidZip || !selectedService} className={`w-full h-16 rounded-xl font-bold text-xl text-white transition-all shadow-lg ${isValidZip && selectedService ? 'bg-[#CC0000] hover:bg-red-800 shadow-red-500/30 hover:shadow-red-500/40 cursor-pointer' : 'bg-gray-300 cursor-not-allowed shadow-none'}`} data-id="element-674">
                  Continue
                </button>

                {/* Trust Badges */}
                <div className="mt-8 flex items-center justify-center gap-6" data-id="element-675">
                  <div className="flex items-center gap-2 bg-yellow-50 px-3 py-1.5 rounded-lg border border-yellow-200" data-id="element-676">
                    <ShieldCheckIcon className="w-5 h-5 text-yellow-600" data-id="element-677" />
                    <span className="text-xs font-bold text-yellow-800 uppercase tracking-tight" data-id="element-678">
                      Lifetime Warranty
                    </span>
                  </div>
                  <div className="flex items-center gap-2 bg-blue-50 px-3 py-1.5 rounded-lg border border-blue-200" data-id="element-679">
                    <div className="font-black text-[#003DA5] text-sm" data-id="element-680">BBB</div>
                    <span className="text-xs font-bold text-blue-800 uppercase tracking-tight" data-id="element-681">
                      A+ Rating
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Red Accent Bar */}
      <div className="w-full h-3 bg-[#CC0000]" data-id="element-682"></div>

      {/* Products Showcase Section */}
      <section className="py-20 bg-white" data-id="element-683">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-684">
          <div className="text-center mb-16" data-id="element-685">
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4" data-id="element-686">
              Our Products
            </h2>
            <p className="text-xl text-gray-600" data-id="element-687">
              Premium solutions for every part of your home
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-688">
            {/* Card 1 */}
            <motion.div className="bg-white rounded-xl overflow-hidden shadow-lg border border-gray-100 flex flex-col" whileHover={{
            y: -5
          }} data-id="element-689">
              <img src="https://cdn.magicpatterns.com/uploads/q7qR5NyHb39LHqKrvgXsGJ/Christina02_TheAfter_BayInterior.jpg" alt="Replacement Windows" className="h-64 w-full object-cover" data-id="element-690" />
              <div className="p-8 flex flex-col flex-1" data-id="element-691">
                <h3 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-692">
                  Replacement Windows
                </h3>
                <p className="text-gray-600 mb-6 flex-1" data-id="element-693">
                  Energy-efficient replacement windows custom-built for your
                  home. Choose from a wide variety of styles, colors, and grid
                  patterns.
                </p>
                <button onClick={() => {
                setSelectedService('windows');
                window.scrollTo({
                  top: 0,
                  behavior: 'smooth'
                });
              }} className="w-full py-3 bg-[#CC0000] hover:bg-red-800 text-white font-bold rounded-lg transition-colors" data-id="element-694">
                  Explore Windows
                </button>
              </div>
            </motion.div>
            {/* Card 2 */}
            <motion.div className="bg-white rounded-xl overflow-hidden shadow-lg border border-gray-100 flex flex-col" whileHover={{
            y: -5
          }} data-id="element-695">
              <img src="https://cdn.magicpatterns.com/uploads/sgJwcsLTG5TY7d8gwqwvoW/Coleman01_TheAfter_AllSeasonExterior_Gable.jpg" alt="Sunrooms" className="h-64 w-full object-cover" data-id="element-696" />
              <div className="p-8 flex flex-col flex-1" data-id="element-697">
                <h3 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-698">
                  Sunrooms
                </h3>
                <p className="text-gray-600 mb-6 flex-1" data-id="element-699">
                  Extend your living space with a beautiful sunroom. Enjoy
                  natural light year-round with our climate-controlled designs.
                </p>
                <button onClick={() => {
                setSelectedService('sunrooms');
                window.scrollTo({
                  top: 0,
                  behavior: 'smooth'
                });
              }} className="w-full py-3 bg-[#CC0000] hover:bg-red-800 text-white font-bold rounded-lg transition-colors" data-id="element-700">
                  Explore Sunrooms
                </button>
              </div>
            </motion.div>
            {/* Card 3 */}
            <motion.div className="bg-white rounded-xl overflow-hidden shadow-lg border border-gray-100 flex flex-col" whileHover={{
            y: -5
          }} data-id="element-701">
              <img src="https://cdn.magicpatterns.com/uploads/pFr3VCZbersDx9RX6LcsuZ/McMahan_after.jpg" alt="Siding & Doors" className="h-64 w-full object-cover" data-id="element-702" />
              <div className="p-8 flex flex-col flex-1" data-id="element-703">
                <h3 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-704">
                  Siding & Doors
                </h3>
                <p className="text-gray-600 mb-6 flex-1" data-id="element-705">
                  Transform your home's curb appeal with premium vinyl siding
                  and elegant glass entry doors.
                </p>
                <button onClick={() => {
                setSelectedService('siding');
                window.scrollTo({
                  top: 0,
                  behavior: 'smooth'
                });
              }} className="w-full py-3 bg-[#CC0000] hover:bg-red-800 text-white font-bold rounded-lg transition-colors" data-id="element-706">
                  Explore Siding & Doors
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose Champion Section */}
      <section className="py-20 bg-[#003DA5] text-white" data-id="element-707">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-708">
          <div className="text-center mb-16" data-id="element-709">
            <h2 className="text-3xl md:text-4xl font-extrabold mb-4" data-id="element-710">
              Why Homeowners Choose Champion
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12" data-id="element-711">
            {[{
            icon: <ZapIcon className="w-8 h-8" data-id="element-712" />,
            stat: 'Up to 31%',
            label: 'Energy Bill Savings',
            desc: 'New products can significantly reduce heating and cooling costs'
          }, {
            icon: <ShieldCheckIcon className="w-8 h-8" data-id="element-713" />,
            stat: '75+',
            label: 'Years of Experience',
            desc: 'A trusted name in home improvement since 1953'
          }, {
            icon: <StarIcon className="w-8 h-8" data-id="element-714" />,
            stat: 'A+',
            label: 'BBB Rating',
            desc: 'Consistently rated A+ by the Better Business Bureau'
          }, {
            icon: <AwardIcon className="w-8 h-8" data-id="element-715" />,
            stat: 'Lifetime',
            label: 'Limited Warranty',
            desc: 'Industry-leading warranty on products and installation'
          }].map((item, idx) => <motion.div key={idx} className="flex flex-col items-center text-center" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.1
          }} data-id="element-716">
                <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mb-6 text-white" data-id="element-717">
                  {item.icon}
                </div>
                <div className="text-4xl md:text-5xl font-black mb-2" data-id="element-718">
                  {item.stat}
                </div>
                <div className="text-lg font-bold mb-3 text-blue-200" data-id="element-719">
                  {item.label}
                </div>
                <div className="text-sm text-blue-100 leading-relaxed" data-id="element-720">
                  {item.desc}
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* What to Expect Section */}
      <section className="py-20 bg-white" data-id="element-721">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-722">
          <div className="text-center mb-16" data-id="element-723">
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900" data-id="element-724">
              What to Expect at Your Free In-Home Estimate
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-center" data-id="element-725">
            {(() => {
            const expectContent: Record<string, Array<{
              title: string;
              desc: string;
              img: string;
            }>> = {
              windows: [{
                title: 'Find The Right Style',
                desc: "Your Champion representative will walk you through window options, custom styles and colors, and warranties. They'll also help you compare the value of Champion.",
                img: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=400&h=400&fit=crop'
              }, {
                title: 'Review Price & Financing',
                desc: "You'll review all current promotions, calculate your estimated monthly payment and energy bill savings, and determine the best financing options for you.",
                img: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=400&h=400&fit=crop'
              }, {
                title: 'Same Day Quote',
                desc: "You'll get an exact, no-obligation quote for both your new windows and installation, and you'll discuss next steps in the window replacement process.",
                img: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=400&h=400&fit=crop'
              }],
              sunrooms: [{
                title: 'Choose Your Sunroom Style',
                desc: 'Your representative will help you design the perfect sunroom for your home, discussing all-season vs three-season options, roof styles, and custom features.',
                img: 'https://images.unsplash.com/photo-1513694203232-719a280e022f?w=400&h=400&fit=crop'
              }, {
                title: 'Review Price & Financing',
                desc: "You'll review current sunroom promotions, calculate your estimated monthly payment, and determine the best financing options for your addition.",
                img: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=400&h=400&fit=crop'
              }, {
                title: 'Same Day Quote',
                desc: "You'll get an exact, no-obligation quote for your custom sunroom and installation, and discuss the timeline for your new living space.",
                img: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=400&h=400&fit=crop'
              }],
              siding: [{
                title: 'Choose Your Siding Style',
                desc: "Explore our premium siding options, colors, and textures. Your representative will help you find the perfect match to enhance your home's curb appeal.",
                img: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=400&h=400&fit=crop'
              }, {
                title: 'Review Price & Financing',
                desc: "You'll review current siding promotions, calculate your estimated monthly payment, and determine the best financing options for your exterior upgrade.",
                img: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=400&h=400&fit=crop'
              }, {
                title: 'Same Day Quote',
                desc: "You'll get an exact, no-obligation quote for your new siding and installation, and discuss the process for transforming your home's exterior.",
                img: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=400&h=400&fit=crop'
              }]
            };
            return (expectContent[selectedService] || expectContent.windows).map((item, idx) => <motion.div key={idx} className="flex flex-col items-center" initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: idx * 0.2,
              duration: 0.5
            }} data-id="element-726">
                  <div className="w-48 h-48 rounded-full overflow-hidden mb-6 shadow-lg border-4 border-white" data-id="element-727">
                    <img src={item.img} alt={item.title} className="w-full h-full object-cover" data-id="element-728" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-729">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed" data-id="element-730">{item.desc}</p>
                </motion.div>);
          })()}
          </div>
        </div>
      </section>

      {/* Before & After Section */}
      <section className="py-20 bg-slate-50" data-id="element-731">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-732">
          <div className="text-center mb-16" data-id="element-733">
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4" data-id="element-734">
              Before &amp; After Transformations
            </h2>
            <p className="text-xl text-gray-600" data-id="element-735">
              See the Champion difference — real results from real homeowners
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10" data-id="element-736">
            {(() => {
            const allItems = [{
              label: 'Kitchen Bay Window Upgrade',
              location: "Christina's Home",
              category: 'windows',
              before: 'https://cdn.magicpatterns.com/uploads/te4bYPC4MgJUDKQ92kUPrs/Christina02_Before_Interior.jpg',
              after: 'https://cdn.magicpatterns.com/uploads/q7qR5NyHb39LHqKrvgXsGJ/Christina02_TheAfter_BayInterior.jpg'
            }, {
              label: 'Double-Hung Window Replacement',
              location: "Cathy's Home",
              category: 'windows',
              before: 'https://cdn.magicpatterns.com/uploads/jiKUPMVfWdBetMKwhZ3CUQ/Cathy04_Before_DoubleHungInterior_Timberbond_CloseupOpen.jpg',
              after: 'https://cdn.magicpatterns.com/uploads/njVFiGkj5VifckHSQKjTXg/Cathy04_TheAfter_DoubleHungInterior_Timberbond_CloseupOpen.jpg'
            }, {
              label: 'All-Season Sunroom Addition',
              location: "Coleman's Home",
              category: 'sunrooms',
              before: 'https://cdn.magicpatterns.com/uploads/sgJwcsLTG5TY7d8gwqwvoW/Coleman01_TheAfter_AllSeasonExterior_Gable.jpg',
              after: 'https://cdn.magicpatterns.com/uploads/sgJwcsLTG5TY7d8gwqwvoW/Coleman01_TheAfter_AllSeasonExterior_Gable.jpg'
            }, {
              label: 'Three-Season Sunroom Addition',
              location: "Peterson's Home",
              category: 'sunrooms',
              before: 'https://cdn.magicpatterns.com/uploads/4cePLWT3uBye8GNuUPSQEV/Peterson01_TheAfter_ThreeSeasonExterior_Gable.jpg',
              after: 'https://cdn.magicpatterns.com/uploads/4cePLWT3uBye8GNuUPSQEV/Peterson01_TheAfter_ThreeSeasonExterior_Gable.jpg'
            }, {
              label: 'Complete Siding Replacement',
              location: "McMahan's Home",
              category: 'siding',
              before: 'https://cdn.magicpatterns.com/uploads/pFr3VCZbersDx9RX6LcsuZ/McMahan_after.jpg',
              after: 'https://cdn.magicpatterns.com/uploads/pFr3VCZbersDx9RX6LcsuZ/McMahan_after.jpg'
            }, {
              label: 'Clapboard Siding Upgrade',
              location: "Perella's Home",
              category: 'siding',
              before: 'https://cdn.magicpatterns.com/uploads/jfn3QjyW6W1bDmCbCVFueH/Perella01_TheAfter_Clapboard.jpg',
              after: 'https://cdn.magicpatterns.com/uploads/jfn3QjyW6W1bDmCbCVFueH/Perella01_TheAfter_Clapboard.jpg'
            }];
            const filteredItems = selectedService ? allItems.filter(item => item.category === selectedService) : allItems;
            return filteredItems.map((item, idx) => <motion.div key={idx} className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-100" initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: idx * 0.15,
              duration: 0.5
            }} data-id="element-737">
                  <div className="grid grid-cols-2" data-id="element-738">
                    <div className="relative" data-id="element-739">
                      <img src={item.before} alt={`Before - ${item.label}`} className="w-full h-52 md:h-64 object-cover" data-id="element-740" />
                      <div className="absolute top-3 left-3 bg-gray-900/80 text-white text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wide" data-id="element-741">
                        Before
                      </div>
                    </div>
                    <div className="relative" data-id="element-742">
                      <img src={item.after} alt={`After - ${item.label}`} className="w-full h-52 md:h-64 object-cover" data-id="element-743" />
                      <div className="absolute top-3 left-3 bg-[#CC0000] text-white text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wide" data-id="element-744">
                        After
                      </div>
                    </div>
                  </div>
                  <div className="px-6 py-5 flex items-center justify-between" data-id="element-745">
                    <div data-id="element-746">
                      <h3 className="font-bold text-gray-900 text-lg" data-id="element-747">
                        {item.label}
                      </h3>
                      <p className="text-sm text-gray-500" data-id="element-748">{item.location}</p>
                    </div>
                    <button onClick={() => setShowForm(true)} className="text-sm font-bold text-[#CC0000] hover:text-red-800 transition-colors flex items-center gap-1" data-id="element-749">
                      Get a Quote <span aria-hidden="true" data-id="element-750">&rarr;</span>
                    </button>
                  </div>
                </motion.div>);
          })()}
          </div>
        </div>
      </section>

      {/* Financing Section */}
      <section className="py-20 bg-white overflow-hidden" data-id="element-751">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-752">
          <div className="flex flex-col md:flex-row items-center gap-12 lg:gap-20" data-id="element-753">
            <div className="flex-1" data-id="element-754">
              <div className="inline-block bg-blue-50 text-[#003DA5] font-bold text-sm px-4 py-2 rounded-full mb-6 border border-blue-100" data-id="element-755">
                Flexible Financing
              </div>
              <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-6" data-id="element-756">
                Pay Nothing for a Full Year**
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed" data-id="element-757">
                We believe every homeowner deserves beautiful, energy-efficient
                home improvements. That's why we offer flexible financing
                options to fit your budget.
              </p>
              <ul className="space-y-4 mb-10" data-id="element-758">
                {['No payments for 12 months with approved credit', 'Low monthly payment options available', 'Quick and easy application process'].map((item, idx) => <li key={idx} className="flex items-start gap-3" data-id="element-759">
                    <div className="mt-1 bg-green-100 rounded-full p-1" data-id="element-760">
                      <CheckCircle2Icon className="w-4 h-4 text-green-600" data-id="element-761" />
                    </div>
                    <span className="text-gray-700 font-medium" data-id="element-762">{item}</span>
                  </li>)}
              </ul>
              <button onClick={() => setShowForm(true)} className="px-8 py-4 bg-[#CC0000] hover:bg-red-800 text-white font-bold text-lg rounded-xl transition-colors shadow-lg shadow-red-500/30" data-id="element-763">
                Get Started Today
              </button>
            </div>
            <div className="flex-1 w-full" data-id="element-764">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl" data-id="element-765">
                <img src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&q=80" alt="Happy family in home" className="w-full h-full object-cover" data-id="element-766" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-[#F5F5F5]" data-id="element-767">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-768">
          <div className="text-center mb-12" data-id="element-769">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900" data-id="element-770">
              See What Our Customers Say
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-771">
            {[{
            name: 'Sarah M.',
            location: 'Columbus, OH',
            text: "The installation crew was professional, clean, and fast. Our new windows look amazing and we've already noticed a difference in our energy bills."
          }, {
            name: 'Robert & Linda T.',
            location: 'Denver, CO',
            text: 'From the initial consultation to the final walkthrough, Champion exceeded our expectations. The quality of the sunroom is outstanding.'
          }, {
            name: 'James W.',
            location: 'Atlanta, GA',
            text: "We replaced 12 windows and a sliding door. The 'Buy 2 Get 2' offer saved us thousands, and the financing made it incredibly affordable."
          }].map((review, idx) => <motion.div key={idx} className="bg-white p-8 rounded-xl shadow-sm border border-gray-200" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.1
          }} data-id="element-772">
                <div className="flex text-yellow-400 mb-4" data-id="element-773">
                  {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-5 h-5 fill-current" data-id="element-774" />)}
                </div>
                <p className="text-gray-600 mb-6 italic" data-id="element-775">"{review.text}"</p>
                <div data-id="element-776">
                  <p className="font-bold text-gray-900" data-id="element-777">{review.name}</p>
                  <p className="text-sm text-gray-500" data-id="element-778">{review.location}</p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Final CTA Banner */}
      <section className="py-16 bg-[#CC0000]" data-id="element-779">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-780">
          <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-6" data-id="element-781">
            Ready to Transform Your Home?
          </h2>
          <p className="text-xl text-red-100 mb-10" data-id="element-782">
            Get your free, no-obligation quote today. Our experts are standing
            by.
          </p>
          <button onClick={() => setShowForm(true)} className="px-10 py-5 bg-white text-[#CC0000] hover:bg-gray-50 font-extrabold text-xl rounded-xl transition-all shadow-xl hover:shadow-2xl hover:-translate-y-1" data-id="element-783">
            Get Your Free Quote
          </button>
          <p className="mt-6 text-sm text-red-200 font-medium" data-id="element-784">
            No obligation • Free in-home estimate • Same-day quote
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-100 py-16 border-t border-gray-200" data-id="element-785">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-786">
          <div className="flex flex-col md:flex-row justify-between items-start gap-12 mb-16" data-id="element-787">
            {/* Logo & Copyright */}
            <div className="flex flex-col gap-8" data-id="element-788">
              <ChampionLogo size="lg" data-id="element-789" />
              <p className="text-xs text-gray-500 font-medium" data-id="element-790">
                © {new Date().getFullYear()} findservicequotes.io. All Rights
                Reserved.
              </p>
            </div>

            {/* Links */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 md:gap-16" data-id="element-791">
              <div className="flex flex-col gap-4" data-id="element-792">
                <a href="#" className="text-xs text-gray-600 hover:text-gray-900 hover:underline font-medium" data-id="element-793">
                  Home
                </a>
                <a href="#" className="text-xs text-gray-600 hover:text-gray-900 hover:underline font-medium" data-id="element-794">
                  Privacy Policy
                </a>
                <a href="#" className="text-xs text-gray-600 hover:text-gray-900 hover:underline font-medium" data-id="element-795">
                  Terms Of Use
                </a>
                <a href="#" className="text-xs text-gray-600 hover:text-gray-900 hover:underline font-medium" data-id="element-796">
                  Cookie Policy
                </a>
                <a href="#" className="text-xs text-gray-600 hover:text-gray-900 hover:underline font-medium" data-id="element-797">
                  Vulnerability Policy
                </a>
              </div>
              <div className="flex flex-col gap-4" data-id="element-798">
                <a href="#" className="text-xs text-gray-600 hover:text-gray-900 hover:underline font-medium" data-id="element-799">
                  Accessibility Policy
                </a>
                <a href="#" className="text-xs text-gray-600 hover:text-gray-900 hover:underline font-medium" data-id="element-800">
                  Do Not Sell or Share My Personal Information
                </a>
                <a href="#" className="text-xs text-gray-600 hover:text-gray-900 hover:underline font-medium" data-id="element-801">
                  Limit the Use of Sensitive Personal Information
                </a>
              </div>
            </div>
          </div>

          {/* Disclaimers */}
          <div className="border-t border-gray-300 pt-8 flex flex-col gap-6" data-id="element-802">
            <p className="text-[10px] text-gray-400 leading-relaxed text-justify" data-id="element-803">
              *Buy 2 Windows, Get 2 Free applies to Champion's standard window
              pricing. Minimum purchase of 4 windows required. Discount applies
              to every other window purchased. **Subject to credit approval.
              Interest is billed during the promotional period, but all interest
              is waived if the purchase amount is paid in full within 12 months.
              Valid on new orders only. Cannot be combined with other offers.
              See representative for details.
            </p>
            <p className="text-[10px] text-gray-400 leading-relaxed text-justify" data-id="element-804">
              **Plan 2521. Subject to credit approval. Rates range from 15.85% -
              21.19% APR (interest rates range from 17.99% - 24.99%). Only
              well-qualified applicants will receive an APR of 15.85%; some
              applicants may not qualify. Loan amount and rate will vary based
              on your income and creditworthiness. 12 month promotional period
              during which interest is billed but will be waived if the amount
              financed is paid in full before Promo Period expires. Monthly
              payments are not required during the Promo Period. Any unpaid
              balance and amounts owed after Promo Period will be paid over 84
              monthly payments.
            </p>
          </div>
        </div>
      </footer>
    </div>;
}